<template>
  <div class="super-assistant">
    <!-- 左侧：历史会话列表 -->
    <div class="assistant-sidebar">
      <div class="sidebar-header">
        <el-button type="primary" size="mini" class="new-conv-button" @click="handleNewConversation">
          新建会话
        </el-button>
        <el-button size="mini" class="clear-conv-button" @click="confirmClearConversations">
          清空会话
        </el-button>
      </div>
      <div class="conversation-list">
        <div v-for="conv in conversationHistory" :key="conv.id" class="conversation-item"
          :class="{ active: activeConvId === conv.id }" @click="setActiveConversation(conv.id)">
          <p class="conversation-title" :title="conv.title">{{ conv.title }}</p>
          <img class="delete-icon"
            src="https://jzguai01.oss-cn-shanghai.aliyuncs.com/uploads/images/20250122/20250122233241207982156.png"
            alt="删除" @click.stop="confirmDeleteConversation(conv.id)" />
        </div>
      </div>
    </div>

    <!-- 右侧主体：聊天记录与输入区 -->
    <div class="assistant-main">
      <div class="chat-header">
        <h2>{{ activeConvTitle }}</h2>
      </div>

      <div class="chat-content" ref="chatContentRef">
        <div v-for="msg in messages" :key="msg.id" :class="['chat-message', msg.role]">
          <div class="message-bubble">
            <!-- 流式加载指示器 -->
            <div v-if="msg.isStreaming" class="streaming-indicator"></div>

            <!-- Markdown 内容渲染 -->
            <div class="markdown-content" v-html="renderMarkdown(msg.text)"></div>
            <!-- 复制按钮（仅限AI回复） -->
            <div v-if="msg.role === 'assistant'" class="copy-wrapper">
              <el-tooltip content="复制内容" placement="top">
                <button class="copy-button" @click.stop="copyText(msg.text)">
                  <el-icon>
                    <DocumentCopy />
                  </el-icon>
                </button>
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-input-container">
        <div class="input-wrapper">
          <el-input v-model="inputText" placeholder="请输入内容" class="input-box" type="input" :disabled="isAIResponding"
            @keydown.enter.prevent="handleSend" />
          <el-button class="send-button" type="primary" :disabled="isAIResponding" @click="handleSend">
            发送
          </el-button>
        </div>
      </div>
    </div>

    <!-- 二次确认弹窗 -->
    <el-dialog v-model="confirmDialogVisible" title="确认操作" width="300px" :close-on-click-modal="false">
      <p>{{ confirmMessage }}</p>
      <template #footer>
        <el-button @click="confirmDialogVisible = false">取消</el-button>
        <el-button style="background-color: #ff5c00;border: none;" type="primary" @click="handleConfirm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from "vue";
import { ElMessage } from "element-plus";
import axios from "axios";
import config from "@/config.js"; // 引入配置文件
import { auth } from "@/utils/auth.js"; // 引入封装的 auth.js
// 在组件中添加
import { marked } from 'marked';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';

// 配置 marked
marked.setOptions({
  highlight: (code, lang) => {
    const language = hljs.getLanguage(lang) ? lang : 'plaintext';
    return hljs.highlight(code, { language }).value;
  },
  breaks: true,
  gfm: true // 启用 GitHub Flavored Markdown
});

export default {
  name: "SuperAssistant",
  data() {
    return {
      conversationHistory: [], // 会话历史
      activeConvId: null, // 当前选中的会话 ID
      messages: [], // 当前会话的消息列表
      inputText: "", // 输入框内容
      confirmDialogVisible: false, // 确认弹窗是否显示
      confirmMessage: "", // 确认弹窗的提示信息
      confirmAction: null, // 确认弹窗的回调函数
      openaiConfig: {}, // OpenAI 配置（url、key、model）
      isAIResponding: false, // 标记 AI 是否正在回复
    };
  },
  setup() {
    const chatContentRef = ref(null);

    // 滚动到底部
    const scrollToBottom = () => {
      nextTick(() => {
        if (chatContentRef.value) {
          chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight;
        }
      });
    };

    onMounted(() => {
      scrollToBottom();
    });

    return { chatContentRef, scrollToBottom };
  },
  computed: {
    // 当前会话的标题
    activeConvTitle() {
      const conv = this.conversationHistory.find(
        (c) => c.id === this.activeConvId
      );
      return conv ? conv.title : "超能助手";
    },
  },
  methods: {

    async copyText(text) {
      try {
        // 移除HTML标签获取纯文本
        const plainText = text.replace(/<[^>]*>?/gm, '')

        // 现代剪贴板API
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(plainText)
        } else {
          // 兼容旧版方法
          const textarea = document.createElement('textarea')
          textarea.value = plainText
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }

        ElMessage.success('复制成功')
      } catch (err) {
        console.error('复制失败:', err)
        ElMessage.error('复制失败，请手动选择内容复制')
      }
    },
    renderMarkdown(text) {
      // 安全过滤 + 生成HTML
      const rawHtml = marked.parse(text || '');
      return DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'p', 'pre', 'code', 'blockquote', 'h1', 'h2', 'h3', 'h4',
          'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'img',
          'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'hr'
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class']
      });
    },
    // 获取用户 ID
    getUserId() {
      const user = auth.getStoredUser(); // 从本地存储获取用户信息
      if (!user || !user.id) {
        ElMessage.error("用户未登录或用户ID无效");
        return null;
      }
      return user.id;
    },

    // 获取 OpenAI 配置
    async fetchOpenAIConfig() {
      try {
        const response = await axios.get(`${config.BASE_URL}/pc/api/get_config_openai.php`);
        if (response.data.success) {
          this.openaiConfig = response.data.data;
        } else {
          ElMessage.error("获取 OpenAI 配置失败");
        }
      } catch (error) {
        console.error("获取 OpenAI 配置失败:", error);
        ElMessage.error("获取 OpenAI 配置失败，请检查网络");
      }
    },

    // 获取会话历史
    async fetchConversations() {
      const userId = this.getUserId();
      if (!userId) return;

      try {
        const response = await axios.post(`${config.BASE_URL}/pc/api/super_assistant_api.php`, {
          action: "get",
          user_id: userId,
        });

        if (response.data.success) {
          this.conversationHistory = response.data.data;
          if (this.conversationHistory.length > 0) {
            this.activeConvId = this.conversationHistory[0].id;
            this.fetchMessages();
          }
        } else {
          ElMessage.error("获取会话历史失败");
        }
      } catch (error) {
        console.error("获取会话历史失败:", error);
        ElMessage.error("获取会话历史失败，请检查网络");
      }
    },

    // 获取消息
    async fetchMessages() {
      const conv = this.conversationHistory.find((c) => c.id === this.activeConvId);
      if (conv && conv.content) {
        this.messages = JSON.parse(conv.content);
      } else {
        this.messages = [];
      }
      this.scrollToBottom();
    },

    // 新建会话
    async handleNewConversation() {
      const userId = this.getUserId();
      if (!userId) return;

      try {
        const response = await axios.post(`${config.BASE_URL}/pc/api/super_assistant_api.php`, {
          action: "add",
          user_id: userId,
          title: "新的会话",
          content: JSON.stringify([]),
        });

        if (response.data.success) {
          await this.fetchConversations();
          ElMessage.success("已新建一个会话");
        } else {
          ElMessage.error("新建会话失败");
        }
      } catch (error) {
        console.error("新建会话失败:", error);
        ElMessage.error("新建会话失败，请检查网络");
      }
    },

    // 清空会话
    async handleClearConversations() {
      const userId = this.getUserId();
      if (!userId) return;

      try {
        const response = await axios.post(`${config.BASE_URL}/pc/api/super_assistant_api.php`, {
          action: "clear",
          user_id: userId,
        });

        if (response.data.success) {
          this.conversationHistory = [];
          this.messages = [];
          ElMessage.success("已清空所有历史会话");
        } else {
          ElMessage.error("清空会话失败");
        }
      } catch (error) {
        console.error("清空会话失败:", error);
        ElMessage.error("清空会话失败，请检查网络");
      }
    },

    // 删除会话
    async deleteConversation(id) {
      const userId = this.getUserId();
      if (!userId) return;

      try {
        const response = await axios.post(`${config.BASE_URL}/pc/api/super_assistant_api.php`, {
          action: "delete",
          user_id: userId,
          id: id,
        });

        if (response.data.success) {
          await this.fetchConversations();
          ElMessage.success("已删除会话");
        } else {
          ElMessage.error("删除会话失败");
        }
      } catch (error) {
        console.error("删除会话失败:", error);
        ElMessage.error("删除会话失败，请检查网络");
      }
    },

    // 修改后的 handleSend 方法
    async handleSend() {
      if (!this.inputText.trim()) {
        ElMessage.warning("请输入内容后再发送");
        return;
      }

      if (this.isAIResponding) {
        ElMessage.warning("AI 正在回复，请稍后再试");
        return;
      }

      // 添加用户消息
      this.messages.push({
        id: Date.now(),
        role: "user",
        text: this.inputText,
      });
      this.inputText = "";
      this.scrollToBottom();

      // 添加初始的AI消息
      const aiMessage = {
        id: Date.now() + 1,
        role: "assistant",
        text: "",
        isStreaming: true // 新增流式标识
      };
      this.messages.push(aiMessage);
      this.isAIResponding = true;

      try {
        // 使用 fetch 实现流式请求
        const response = await fetch(this.openaiConfig.openai_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${this.openaiConfig.openai_key}`
          },
          body: JSON.stringify({
            model: this.openaiConfig.openai_model,
            messages: this.messages
              .filter(m => !m.isStreaming)
              .map(m => ({ role: m.role, content: m.text })),
            stream: true // 启用流式响应
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;

          if (value) {
            // 处理流式数据
            const chunk = decoder.decode(value);
            const lines = chunk.split('\n').filter(line => line.trim() !== '');

            for (const line of lines) {
              const message = line.replace(/^data: /, '');
              if (message === '[DONE]') break;

              try {
                const parsed = JSON.parse(message);
                const content = parsed.choices[0]?.delta?.content || '';

                // 更新最后一条消息的内容
                const lastMsg = this.messages[this.messages.length - 1];
                if (lastMsg) {
                  lastMsg.text += content;
                  this.scrollToBottom();
                }
              } catch (err) {
                console.error('解析错误:', err);
              }
            }
          }
        }

      } catch (error) {
        console.error("流式请求失败:", error);
        ElMessage.error("回复生成失败");
        // 移除流式消息
        this.messages = this.messages.filter(m => !m.isStreaming);
      } finally {
        this.isAIResponding = false;
        // 移除流式标识并保存
        this.messages.forEach(m => delete m.isStreaming);
        this.updateConversationContent();
      }
    },
    // 更新会话内容
    async updateConversationContent() {
      const conv = this.conversationHistory.find((c) => c.id === this.activeConvId);
      if (conv) {
        const userId = this.getUserId();
        if (!userId) return;

        // 如果会话标题为空或为默认值 "新的会话"，取第一条消息的 text 作为标题
        if ((!conv.title || conv.title === "新的会话") && this.messages.length > 0) {
          conv.title = this.messages[0].text.substring(0, 50); // 截取前50个字符作为标题
        }

        try {
          const response = await axios.post(`${config.BASE_URL}/pc/api/super_assistant_api.php`, {
            action: "update",
            user_id: userId,
            id: conv.id,
            title: conv.title, // 更新标题
            content: JSON.stringify(this.messages),
          });

          if (!response.data.success) {
            ElMessage.error("更新会话内容失败");
          }
        } catch (error) {
          console.error("更新会话内容失败:", error);
          ElMessage.error("更新会话内容失败，请检查网络");
        }
      }
    },

    // 设置当前会话
    setActiveConversation(id) {
      this.activeConvId = id;
      this.fetchMessages();
    },

    // 确认删除会话
    confirmDeleteConversation(id) {
      this.confirmMessage = "确定要删除该会话吗？";
      this.confirmAction = () => this.deleteConversation(id);
      this.confirmDialogVisible = true;
    },

    // 确认清空会话
    confirmClearConversations() {
      this.confirmMessage = "确定要清空所有会话吗？";
      this.confirmAction = this.handleClearConversations;
      this.confirmDialogVisible = true;
    },

    // 确认弹窗的回调
    handleConfirm() {
      if (this.confirmAction) {
        this.confirmAction();
      }
      this.confirmDialogVisible = false;
    },
  },
  async created() {
    await this.fetchOpenAIConfig(); // 获取 OpenAI 配置
    this.fetchConversations(); // 获取会话历史
  },
};
</script>

<style scoped>
.super-assistant {
  display: flex;
  height: 100vh;
  box-sizing: border-box;
}

/* 左侧侧边栏 */
.assistant-sidebar {
  width: 220px;

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.sidebar-header {
  padding: 10px;
  display: flex;
  justify-content: space-between;
}

.new-conv-button,
.clear-conv-button {
  background-color: #ff5c00;
  border-color: #ff5c00;
  color: white;
}

.new-conv-button:hover,
.clear-conv-button:hover {
  background-color: #e85500;
}

.conversation-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.conversation-item {
  padding-right: 8px;
  padding-left: 8px;
  margin-bottom: 5px;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f5f7fa;
  cursor: pointer;
}

.conversation-item:hover {
  background-color: #e4e7ed;
}

.conversation-item.active {
  background-color: #ffe6cc !important;
  color: #ff5c00;
}

.conversation-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.delete-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-left: 10px;
}

.delete-icon:hover {
  transform: scale(1.1);
}

/* 右侧主体 */
.assistant-main {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chat-header {

  padding: 10px;
  font-size: 16px;
  font-weight: bold;
  background: linear-gradient(50deg, #ffffff, #ffffff);

  /* 柔和渐变色 */
  color: #4a4a4a;
  /* 深灰字体颜色，增强可读性 */
  border-top-right-radius: 12px;
  /* 右上角圆角 */
  border-top-left-radius: 12px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  /* 添加轻微的阴影效果 */
  overflow: hidden;
  /* 确保圆角生效 */
}

.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: linear-gradient(120deg, #ffffff, #ffffff);
  /* 明亮清新的渐变 */

  /* 内部圆角 */
  box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.1);
  /* 添加轻微内阴影 */
}

.chat-message {
  margin-bottom: 12px;
  display: flex;
  gap: 10px;
}

.chat-message.user {
  justify-content: flex-end;
}

/* 添加样式 */
.copy-wrapper {
  position: absolute;
  right: 10px;
  bottom: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-bubble:hover .copy-wrapper {
  opacity: 1;
}

.copy-button {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.copy-button:hover {
  background: #fff;
  border-color: #ff5c00;
  color: #ff5c00;
  transform: scale(1.1);
}

/* 调整消息气泡定位 */
.message-bubble {
  position: relative;
  padding: 12px 40px 12px 12px;
  /* 右侧留出复制按钮空间 */
}

.chat-message.user .message-bubble {
  background: linear-gradient(45deg, #ff8a00, #ff5c00);
  color: white;
  border-radius: 15px;
  padding: 10px;
  max-width: 60%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.chat-message.assistant .message-bubble {
  background: #f0f0f0;
  border-radius: 15px;
  padding: 10px;
  max-width: 60%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Markdown 通用样式 */
.markdown-content {
  line-height: 1.6;
  overflow-x: auto;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3 {
  margin: 1em 0;
  border-bottom: 1px solid #eee;
}

.markdown-content code {
  background-color: #f3f4f6;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: Monaco, Consolas, monospace;
}

.markdown-content pre {
  background-color: #f8f8f8;
  padding: 1em;
  border-radius: 8px;
  margin: 1em 0;
  overflow-x: auto;
}

.markdown-content pre code {
  background: none;
  padding: 0;
}

.markdown-content blockquote {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding-left: 1em;
  color: #666;
}

.markdown-content table {
  border-collapse: collapse;
  margin: 1em 0;
}

.markdown-content th,
.markdown-content td {
  border: 1px solid #ddd;
  padding: 8px 12px;
}

/* 代码高亮主题 */
.hljs {
  display: block;
  background: #f8f8f8;
  padding: 1em;
  border-radius: 4px;
}

/* 流式加载指示器 */
.streaming-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  background: #ff5c00;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
  margin-left: 8px;
  vertical-align: middle;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }

  50% {
    transform: scale(1.1);
    opacity: 1;
  }

  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}

.chat-input-container {
  background: #f8f8f8;

}

.input-wrapper {
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: #fff;
  /* 设置背景色 */
}

.input-box {
  flex: 1;
  border: none;
  resize: none;
  /* 禁止拉伸输入框 */
  font-size: 14px;
  height: 60px;
  /* 调整输入框高度 */
  background-color: #f9f9f9;
  /* 设置背景色 */
  transition: border-color 0.3s ease;
  /* 添加过渡效果 */
}

.input-box:focus {
  outline: none;
  border-color: #ff5c00;
  /* 聚焦时改变边框颜色 */
}

.send-button {
  height: 60px;
  /* 调整按钮高度 */
  width: 120px;
  /* 调整按钮宽度 */

  border: none;
  background: linear-gradient(45deg, #ff8a00, #ff5c00);
  /* 渐变背景色 */
  color: white;
  font-size: 14px;
  font-weight: bold;
  margin-left: 10px;
  /* 调整按钮与输入框的间距 */
  cursor: pointer;
  transition: background 0.3s ease, transform 0.2s ease;
  /* 添加过渡效果 */
}

.send-button:hover {
  background: linear-gradient(45deg, #ff5c00, #ff3a00);
  /* 悬停时改变背景色 */
  transform: translateY(-1px);
  /* 悬停时轻微上移 */
}

.send-button:active {
  transform: translateY(1px);
  /* 点击时轻微下移 */
}

/* 加载效果 */
.thinking-text {
  color: #666;
  font-style: italic;
}

/* 打字效果 */
.typing-text {
  position: relative;
  display: inline-block;
}

.typing-cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: #000;
  animation: blink 1s infinite;
}

.input-box:disabled {
  background-color: #f0f0f0;
  /* 禁用时的背景色 */
  cursor: not-allowed;
  /* 禁用时的鼠标样式 */
}

.send-button:disabled {
  background: #ccc;
  /* 禁用时的背景色 */
  cursor: not-allowed;
  /* 禁用时的鼠标样式 */
  opacity: 0.7;
  /* 降低透明度 */
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}
</style>
